import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useHealth, useEventStream, useUpdateAvailable } from "../api";
import { LoginScreen, useAuthState } from "../auth-ui";
import { Logo } from "../components/Logo";
import { useVocab } from "../vocab";

export function AppShell() {
  const auth = useAuthState();
  const { simple, setSimple } = useVocab();
  const updateAvailable = useUpdateAvailable();

  if (auth.isLoading) return <div className="loading">Loading...</div>;
  if (auth.data && !auth.data.authenticated) {
    return (
      <>
        <LoginScreen setupRequired={auth.data.setupRequired === true} />
        {updateAvailable && <UpdateToast />}
      </>
    );
  }

  return (
    <>
      <AuthedShell simple={simple} setSimple={setSimple} />
      {updateAvailable && <UpdateToast />}
    </>
  );
}

function UpdateToast() {
  return (
    <div className="update-toast" role="status">
      <span>
        IdeaCrunch was updated.{" "}
        <span style={{ color: "var(--muted)" }}>
          Refresh to get the new version.
        </span>
      </span>
      <button className="btn" onClick={() => window.location.reload()}>
        Refresh
      </button>
    </div>
  );
}

function AuthedShell({
  simple,
  setSimple,
}: {
  simple: boolean;
  setSimple: (v: boolean) => void;
}) {
  useEventStream();
  const health = useHealth();
  const engineEnabled = health.data?.engineEnabled === true;
  // Mobile-only hamburger state; the toggle button is display:none on
  // desktop, where the rail is always fully visible.
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="shell">
      <nav className={`rail ${menuOpen ? "open" : ""}`}>
        <div className="brand">
          <Logo size={22} />
          IdeaCrunch
        </div>
        <button
          className="rail-toggle"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? "✕" : "☰"}
        </button>
        <NavLink to="/new" className="new-idea nav-item" onClick={closeMenu}>
          + New idea
        </NavLink>
        <NavLink
          to="/"
          end
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          onClick={closeMenu}
        >
          Ideas
        </NavLink>
        <NavLink
          to="/library"
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          onClick={closeMenu}
        >
          Library
        </NavLink>
        <NavLink
          to="/settings"
          className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
          onClick={closeMenu}
        >
          Settings
        </NavLink>
        <div className="rail-footer">
          <label className="toggle-row" title="Plain language, short summaries first, detail collapsed">
            <input
              type="checkbox"
              checked={simple}
              onChange={(e) => setSimple(e.target.checked)}
            />
            Simple mode
          </label>
          <span className={`engine-pill ${engineEnabled ? "online" : ""}`}>
            <span className="dot" />{" "}
            {engineEnabled ? "Engine ready" : "Engine offline"}
          </span>
        </div>
      </nav>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}
